import React, { useState, useEffect } from 'react';
import { charactersAPI } from '../../services/api';
import './CharacterCreationModal.css'; // Reuse same styling

function CharacterSelectionModal({ 
  isOpen, 
  onClose, 
  campaignData, 
  onCharacterSelected, 
  onCreateNew 
}) {
  const [characters, setCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState(null);

  const { campaignId, campaignName, startingXp = 1200 } = campaignData || {};

  useEffect(() => {
    if (isOpen) {
      loadAvailableCharacters();
    }
    // reset selection/errors when reopened
    if (isOpen) {
      setSelectedCharacterId(null);
      setError(null);
    }
  }, [isOpen]);

  const loadAvailableCharacters = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await charactersAPI.getMyCharacters();
      
      // Filter to only show characters not already in a campaign
      const availableChars = (res.data?.characters || []).filter(
        char => !char.campaignId || char.campaignId === null
      );
      
      setCharacters(availableChars);
    } catch (err) {
      console.error('Failed to load characters:', err);
      setError(err?.response?.data?.error || 'Failed to load characters');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCharacter = async () => {
    if (!selectedCharacterId) return;

    try {
      if (!campaignId) {
        setError('Campaign context missing. Please close and re-open the invite flow.');
        return;
      }

      // IMPORTANT:
      // Do NOT call charactersAPI.updateCharacter here.
      // CharacterController intentionally blocks changing campaignId via updateCharacter,
      // which previously caused "No fields to update".
      // The parent (CampaignInvitations) will attach the character via the invitations endpoint.
      const selectedChar = characters.find(c => c.id === selectedCharacterId);
      if (!selectedChar) {
        setError('Selected character not found. Please refresh and try again.');
        return;
      }

      onCharacterSelected?.(selectedChar);
      onClose?.();
    } catch (err) {
      console.error('Failed to select character:', err);
      setError(err?.response?.data?.error || 'Failed to select character');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="modal-container character-creation-modal">
        <div className="modal-header">
          <h3>Join {campaignName}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          <div className="campaign-info-box">
            <p className="info-text">
              Choose an existing character or create a new one for this campaign.
            </p>
            <p className="info-text" style={{ marginTop: '8px' }}>
              <strong>Campaign Starting XP:</strong> {startingXp} XP
            </p>
          </div>

          {isLoading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>Loading your characters...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : characters.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ marginBottom: '20px' }}>
                You don't have any available characters. Create a new one to join this campaign!
              </p>
              <button 
                className="btn-primary"
                onClick={() => {
                  onClose?.();
                  onCreateNew?.();
                }}
              >
                Create New Character
              </button>
            </div>
          ) : (
            <>
              <div className="form-field">
                <label>Select an Existing Character</label>
                <div className="character-list" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '10px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '10px',
                  border: '1px solid rgba(123, 44, 191, 0.3)',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(15, 5, 29, 0.4)'
                }}>
                  {characters.map(character => (
                    <div 
                      key={character.id}
                      className={`type-card ${selectedCharacterId === character.id ? 'selected' : ''}`}
                      onClick={() => setSelectedCharacterId(character.id)}
                      style={{ 
                        cursor: 'pointer',
                        marginBottom: '0'
                      }}
                    >
                      <input
                        type="radio"
                        name="characterSelect"
                        value={character.id}
                        checked={selectedCharacterId === character.id}
                        onChange={() => setSelectedCharacterId(character.id)}
                      />
                      <div className="type-content" style={{ textAlign: 'left' }}>
                        <h4>{character.name}</h4>
                        <p className="type-cost">Type: {character.characterType}</p>
                        <p className="type-available">
                          Available XP: {character.availableXp} | 
                          Level: {character.level || 1} | 
                          Primary: {getPrimaryStat(character)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: 'rgba(123, 44, 191, 0.1)',
                borderRadius: '5px',
                textAlign: 'center'
              }}>
                <p style={{ marginBottom: '10px' }}>Or create a new character for this campaign:</p>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    onClose?.();
                    onCreateNew?.();
                  }}
                  style={{ width: '100%' }}
                >
                  Create New Character
                </button>
              </div>

              <div className="modal-actions" style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSelectCharacter}
                  disabled={!selectedCharacterId}
                >
                  Use Selected Character
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to calculate primary stat
function getPrimaryStat(character) {
  const stats = {
    Physique: character.physique || 0,
    Coordination: character.coordination || 0,
    Logic: character.logic || 0,
    Perception: character.perception || 0,
    Presence: character.presence || 0,
    Resilience: character.resilience || 0
  };
  
  let maxStat = 'Physique';
  let maxValue = stats.Physique;
  
  Object.entries(stats).forEach(([stat, value]) => {
    if (value > maxValue) {
      maxValue = value;
      maxStat = stat;
    }
  });
  
  return maxStat;
}

export default CharacterSelectionModal;
